/* eslint-disable react/prop-types */
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'
import {
  CampaignReadyModal,
  DiscountModal,
  ExitFeedbackModal,
  PaymentSuccessModal,
} from '../../../../components/CampaignUpsellModals'
import {
  UPSELL_FLOWS,
  useUpsellCatalog,
  useUpgradePlansTab,
  useUpsellEventMap,
  useUpsellFlowState,
  useUpsellCheckout,
  useUpsellSuccessClose,
  trackUpsellExitFeedback,
  resolvePlanCheckoutPayload,
  resolvePackageCheckoutPayload,
} from '../UpsellFlows'
import {
  UPSELL_FEATURED_PACKAGE_CODE,
  UPSELL_FEATURED_PLAN_TIER,
  filterStaticPlanOptions,
} from '../UpsellFlows/upsellCatalog'
import {
  EXIT_FEEDBACK_OPTIONS,
  UPSELL_TABS,
} from '../UpsellFlows/sharedConstants'
import {
  PLANS_OTHER_OPTIONS,
  PRO_UPGRADE,
  RECHARGE_DOWNSELL_OFFER,
  SUBSCRIBED_ARTS_LIMIT_STEPS,
  SUBSCRIBED_UPGRADE_BONUS_OFFER_CODE,
  UPGRADE_BONUS_OFFER,
} from './constants'

const CONTINUE_CREATING_ACTION = { label: 'Continuar criando' }

export const SubscribedArtsLimitUpsellFlow = forwardRef(
  function SubscribedArtsLimitUpsellFlow(
    { enabled = false, campaignId = null, onDismiss },
    ref,
  ) {
    const catalog = useUpsellCatalog({ enabled })
    const {
      openSubscriptionCheckout,
      openAddonCheckout,
    } = useUpsellCheckout()
    const {
      activeTab,
      setActiveTab,
      closeFlow: resetFlow,
      openFlow,
      isStep,
      goToStep,
      openSuccessStep,
      currentStep,
      isSuccessStepActive,
    } = useUpsellFlowState({
      enabled,
      initialStep: SUBSCRIBED_ARTS_LIMIT_STEPS.READY,
      initialTab: 'plans',
    })
    const upsellTabs = useUpgradePlansTab(catalog, activeTab, setActiveTab, UPSELL_TABS)

    useUpsellEventMap({
      flow: UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT,
      campaignId,
      currentStep,
      activeTab,
    })

    const upgradeOffer = useMemo(
      () =>
        catalog.buildUpgradeFeaturedOffer() ?? {
          label: `Upgrade para o Pro · ${PRO_UPGRADE.artsPerMonth} artes/mês`,
          price: PRO_UPGRADE.price,
          unitPrice: PRO_UPGRADE.unitPrice,
          description: PRO_UPGRADE.description,
          name: PRO_UPGRADE.name,
          artsPerMonth: PRO_UPGRADE.artsPerMonth,
        },
      [catalog],
    )

    const rechargeOffer = useMemo(
      () => catalog.buildRechargeFeaturedOffer(),
      [catalog],
    )

    const featuredUpgradePlan = catalog.featuredUpgradePlan

    const plansOtherOptions = useMemo(() => {
      const featuredTier =
        catalog.featuredUpgradePlan?.tier ?? UPSELL_FEATURED_PLAN_TIER

      if (!catalog.dbPlans.length) {
        return filterStaticPlanOptions(
          PLANS_OTHER_OPTIONS,
          catalog.currentPlanTier,
          featuredTier,
        )
      }

      return catalog.getUpgradePlanOptions()
    }, [catalog])

    const packageOptions = useMemo(
      () => catalog.getPackageOptions([UPSELL_FEATURED_PACKAGE_CODE]),
      [catalog],
    )

    const rechargeArts = rechargeOffer?.arts
    const upgradeBonusOffer = useMemo(
      () => ({
        ...UPGRADE_BONUS_OFFER,
        label: upgradeOffer.artsPerMonth
          ? `${upgradeOffer.name ?? PRO_UPGRADE.name} · ${upgradeOffer.artsPerMonth} artes + 20 de bônus no 1º mês`
          : UPGRADE_BONUS_OFFER.label,
        price: upgradeOffer.price ?? UPGRADE_BONUS_OFFER.price,
      }),
      [upgradeOffer],
    )

    const rechargeDownsellOffer = useMemo(
      () => ({
        ...RECHARGE_DOWNSELL_OFFER,
        description:
          rechargeArts != null
            ? `${rechargeArts} artes`
            : RECHARGE_DOWNSELL_OFFER.description,
        price: rechargeOffer?.price ?? RECHARGE_DOWNSELL_OFFER.price,
      }),
      [rechargeArts, rechargeOffer],
    )

    const hideFlow = resetFlow

    const closeFlow = useCallback(() => {
      resetFlow()
      onDismiss?.()
    }, [onDismiss, resetFlow])

    const closeSuccessFlow = useUpsellSuccessClose(closeFlow, isSuccessStepActive)

    const handleOpenPackageCheckout = useCallback((option) => {
      openAddonCheckout({
        ...resolvePackageCheckoutPayload(
          option,
          catalog.featuredPackage,
          UPSELL_FEATURED_PACKAGE_CODE,
        ),
        campaignArtGenerationId: campaignId,
        upsellFlow: UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT,
        upsellSuccessStep: SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_RECHARGE,
        onOpen: hideFlow,
      })
    }, [campaignId, catalog.featuredPackage, hideFlow, openAddonCheckout])

    const handleOpenPlanCheckout = useCallback((
      option,
      {
        upsellSuccessStep = SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE,
        offerCode = null,
      } = {},
    ) => {
      openSubscriptionCheckout({
        ...resolvePlanCheckoutPayload(option, featuredUpgradePlan),
        campaignArtGenerationId: campaignId,
        upsellFlow: UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT,
        upsellSuccessStep,
        offerCode,
        onOpen: hideFlow,
      })
    }, [campaignId, featuredUpgradePlan, hideFlow, openSubscriptionCheckout])

    const handleOpenFeaturedPackageCheckout = useCallback(() => {
      handleOpenPackageCheckout(catalog.featuredPackage)
    }, [catalog.featuredPackage, handleOpenPackageCheckout])

    const handleOpenFeaturedPlanCheckout = useCallback(() => {
      handleOpenPlanCheckout(featuredUpgradePlan, {
        upsellSuccessStep: SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE,
      })
    }, [featuredUpgradePlan, handleOpenPlanCheckout])

    const handleOpenUpgradeBonusPlanCheckout = useCallback(() => {
      handleOpenPlanCheckout(featuredUpgradePlan, {
        upsellSuccessStep: SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE_BONUS,
        offerCode: SUBSCRIBED_UPGRADE_BONUS_OFFER_CODE,
      })
    }, [featuredUpgradePlan, handleOpenPlanCheckout])

    const handleOpenPlanFromList = useCallback((option) => {
      handleOpenPlanCheckout(option, {
        upsellSuccessStep: SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE,
      })
    }, [handleOpenPlanCheckout])

    const withClose = (action) => ({
      ...action,
      onClick: closeSuccessFlow,
    })

    useImperativeHandle(ref, () => ({
      open: openFlow,
      close: closeFlow,
      openSuccessStep,
    }), [closeFlow, openFlow, openSuccessStep])

    if (!enabled && !isSuccessStepActive) {
      return null
    }

    const readyTitle = 'Suas artes acabaram.'
    const readySubtitle = 'Sua operação não precisa parar.'

    const plansContent = {
      title: readyTitle,
      subtitle: readySubtitle,
      marketLabel: 'Agência ou social media',
      marketPrice: 'R$ 2.000 a R$ 3.000 /mês',
      platformLabel: upgradeOffer.name
        ? `Upgrade ${upgradeOffer.name}`
        : 'Upgrade Pro',
      platformPrice: upgradeOffer.price?.replace('/mês', ' /mês'),
      featuredOffer: upgradeOffer,
      otherOptionsTitle: 'Outras opções',
      otherOptions: plansOtherOptions,
      onSelectOption: handleOpenPlanFromList,
      primaryAction: {
        label: upgradeOffer.name
          ? `Fazer upgrade para o ${upgradeOffer.name}`
          : 'Fazer upgrade',
        onClick: handleOpenFeaturedPlanCheckout,
      },
      tertiaryLink: {
        label: 'Ver outras opções de pacotes para recarga avulsa →',
        onClick: () => setActiveTab('packages'),
      },
      skipAction: {
        label: 'Continuar sem gerar artes',
        onClick: () => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.UPGRADE_BONUS),
      },
    }

    const packagesContent = {
      title: readyTitle,
      subtitle: readySubtitle,
      marketLabel: '25 artes com um designer',
      marketPrice: 'a partir de R$ 1.250',
      platformLabel: 'No Xamã',
      platformPrice: rechargeOffer?.price,
      featuredOffer: rechargeOffer,
      otherOptionsTitle: 'Outras opções de pacotes avulsos',
      otherOptions: packageOptions,
      onSelectOption: handleOpenPackageCheckout,
      primaryAction: {
        label: rechargeArts != null ? `Recarregar ${rechargeArts} artes` : 'Recarregar artes',
        onClick: handleOpenFeaturedPackageCheckout,
      },
      skipAction: {
        label: 'Continuar sem gerar artes',
        onClick: () => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.UPGRADE_BONUS),
      },
    }

    return (
      <>
        <CampaignReadyModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.READY)}
          onClose={() => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.UPGRADE_BONUS)}
          tabs={upsellTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          packages={packagesContent}
          plans={plansContent}
        />

        <DiscountModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.UPGRADE_BONUS)}
          onClose={() => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.RECHARGE)}
          title="Upgrade para o Pro com +20 artes"
          subtitle="Bônus válido no seu primeiro mês."
          offer={upgradeBonusOffer}
          primaryAction={{
            label: 'Upgrade para o Pro',
            onClick: handleOpenUpgradeBonusPlanCheckout,
          }}
          dismissAction={{
            label: 'Agora não',
            onClick: () => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.RECHARGE),
          }}
        />

        <DiscountModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.RECHARGE)}
          onClose={() => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.EXIT)}
          title="Sem mudar de plano"
          subtitle="Recarregue e siga criando hoje mesmo."
          offer={rechargeDownsellOffer}
          footnote="Você recebe um aviso automático quando os créditos do plano renovarem."
          primaryAction={{
            label: rechargeArts != null ? `Recarregar ${rechargeArts} artes` : 'Recarregar artes',
            onClick: handleOpenFeaturedPackageCheckout,
          }}
          dismissAction={{
            label: 'Sair mesmo assim',
            onClick: () => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.EXIT),
          }}
        />

        <ExitFeedbackModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.EXIT)}
          onClose={closeFlow}
          title="Combinado!"
          description="Avisaremos você quando os créditos renovarem. Todo o seu trabalho está salvo."
          feedback={{
            options: EXIT_FEEDBACK_OPTIONS,
          }}
          onExitWithFeedback={(reason) =>
            trackUpsellExitFeedback({
              flow: UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT,
              campaignId,
              reasonId: reason.id,
              reasonLabel: reason.label,
            })
          }
          secondaryAction={{
            label: 'Voltar para a plataforma',
            onClick: closeFlow,
          }}
        />

        <PaymentSuccessModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE)}
          onClose={closeSuccessFlow}
          title="Upgrade feito!"
          description={`Bem-vindo ao ${upgradeOffer.name ?? PRO_UPGRADE.name}: ${upgradeOffer.artsPerMonth ?? PRO_UPGRADE.artsPerMonth} artes por mês. Suas artes já estão na conta.`}
          primaryAction={withClose(CONTINUE_CREATING_ACTION)}
        />

        <PaymentSuccessModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE_BONUS)}
          onClose={closeSuccessFlow}
          title="Upgrade feito!"
          description={`Bem-vindo ao ${upgradeOffer.name ?? PRO_UPGRADE.name}: ${upgradeOffer.artsPerMonth ?? PRO_UPGRADE.artsPerMonth} artes por mês + 20 bônus no 1º mês. Suas artes já estão na conta.`}
          primaryAction={withClose(CONTINUE_CREATING_ACTION)}
        />

        <PaymentSuccessModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_RECHARGE)}
          onClose={closeSuccessFlow}
          title="Recarga feita!"
          description={
            rechargeArts != null
              ? `${rechargeArts} artes somadas ao seu saldo. Seu plano continua o mesmo.`
              : 'Artes somadas ao seu saldo. Seu plano continua o mesmo.'
          }
          primaryAction={withClose(CONTINUE_CREATING_ACTION)}
        />
      </>
    )
  },
)

export default SubscribedArtsLimitUpsellFlow
